import React, { useEffect, useMemo, useState } from 'react'
import { CALCULATION_VERSION, TOOL_CURRENCIES, calculateRoi, type CalculationScenario } from '../../../../shared/workforceCalculators.mjs'
import { StaffingPlanningTools } from './StaffingPlanningTools'
import type { RoiCalculatorContent } from './nexusLandingContent'
import { currentLeadAttribution, submitRoiReport } from '../../services/contact'

type RoiSectionProps = {
  content: RoiCalculatorContent
}

const formatCurrency = (value: number, locale: string, currency = 'USD') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value)
}

type RoiInputProps = {
  id: string
  label: string
  value: number
  min: number
  max: number
  step?: number
  prefix?: string
  onChange: (value: number) => void
}

const RoiInput: React.FC<RoiInputProps> = ({ id, label, value, min, max, step = 1, prefix, onChange }) => {
  const handleChange = (nextValue: string) => {
    const parsed = Number(nextValue)
    if (Number.isFinite(parsed)) {
      onChange(Math.min(max, Math.max(min, parsed)))
    }
  }

  return (
    <label className="landing-roi-input" htmlFor={id}>
      <span>{label}</span>
      <div className="landing-roi-input__row">
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(event) => handleChange(event.target.value)}
        />
        <div className="landing-roi-number">
          {prefix ? <span>{prefix}</span> : null}
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(event) => handleChange(event.target.value)}
            aria-label={label}
          />
        </div>
      </div>
    </label>
  )
}

export const RoiSection: React.FC<RoiSectionProps> = ({ content }) => {
  const [workers, setWorkers] = useState(120)
  const [locations, setLocations] = useState(6)
  const [adminHours, setAdminHours] = useState(12)
  const [adminRate, setAdminRate] = useState(32)
  const [supervisorHours, setSupervisorHours] = useState(4)
  const [reductionPercent, setReductionPercent] = useState(25)
  const [approvalHours, setApprovalHours] = useState(0)
  const [correctionFrequency, setCorrectionFrequency] = useState(0)
  const [correctionCost, setCorrectionCost] = useState(0)
  const [currency, setCurrency] = useState('USD')
  const [period, setPeriod] = useState<'weekly'|'monthly'|'annual'>('annual')
  const [otherScenarios,setOtherScenarios] = useState<CalculationScenario[]>([])
  const [followUpConsent,setFollowUpConsent] = useState(false)
  const [submissionId,setSubmissionId] = useState(()=>crypto.randomUUID())
  const [tools,setTools] = useState<{roi:{enabled:boolean};staffing:{enabled:boolean}}|null>(null)
  const [toolsError,setToolsError] = useState(false)
  const [reportForm, setReportForm] = useState({ company: '', contact: '', email: '', industry: '' })
  const [reportStatus, setReportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const locale = document.documentElement.lang || 'en'
  useEffect(()=>{
    const controller=new AbortController()
    fetch('/api/public/workforce-tools',{credentials:'omit',signal:controller.signal}).then(response=>{if(!response.ok)throw new Error();return response.json()}).then(setTools).catch(()=>{if(!controller.signal.aborted)setToolsError(true)})
    return()=>controller.abort()
  },[])
  const calculationInputs = useMemo(()=>({adminHours,adminRate,supervisorHours,approvalHours,correctionFrequency,correctionCost,reductionPercent}),[adminHours,adminRate,supervisorHours,approvalHours,correctionFrequency,correctionCost,reductionPercent])
  const calculation = useMemo(()=>calculateRoi(calculationInputs),[calculationInputs])
  const results = useMemo(() => {
    return {
      ...calculation.baseline,
      reclaimed: calculation.potentialSavings.annual,
    }
  }, [calculation])

  // Workforce scale informs the ROI calculation but is not a commercial
  // capacity tier. Every lead starts with Free; Sales qualifies Acceleration
  // from requested speed, automation, integrations, and enterprise controls.
  const pricingRecommendation = 'free'

  const submitReport = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setReportStatus('loading')
    try {
      await submitRoiReport({
        ...reportForm,
        workers,
        locations,
        estimatedSavings: results.reclaimed,
        pricingRecommendation,
        calculationSummaries: [{version:CALCULATION_VERSION,tool:'roi',currency,reportingPeriod:period,inputs:calculationInputs},...(tools?.staffing.enabled?otherScenarios:[])],
        commercialFollowUpConsent:followUpConsent,
        publicCalculationSubmissionId:submissionId,
        ...currentLeadAttribution(),
      })
      setReportForm({ company: '', contact: '', email: '', industry: '' })
      setReportStatus('success')
      setSubmissionId(crypto.randomUUID())
    } catch {
      setReportStatus('error')
    }
  }

  return (
    <section id="roi" className="landing-roi-strip">
      <div className="container landing-roi-calculator">
        <div className="landing-roi-calculator__copy">
          <p className="landing-eyebrow">{content.eyebrow}</p>
          <h2>{content.title}</h2>
          <p>{content.subtitle}</p>
          <a className="btn btn-primary" href={content.cta.href}>{content.cta.label}</a>
        </div>

        <div className="landing-roi-calculator__inputs" aria-label={content.assumptionsLabel}>
          <p>Example starting values are editable. Hours are aggregate weekly totals across the workforce; worker/site counts are context and do not multiply cost. Enter non-overlapping administration, supervisor, approval and correction effort.</p>
          <label>Currency<select value={currency} onChange={event=>setCurrency(event.target.value)}>{TOOL_CURRENCIES.map(value=><option key={value} value={value}>{value}</option>)}</select></label>
          <label>Reporting period<select value={period} onChange={event=>setPeriod(event.target.value as typeof period)}><option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="annual">Annual</option></select></label>
          <RoiInput id="roi-workers" label={content.inputs.workers} min={1} max={2000} value={workers} onChange={setWorkers} />
          <RoiInput id="roi-locations" label={content.inputs.locations} min={1} max={250} value={locations} onChange={setLocations} />
          <RoiInput id="roi-admin-hours" label={content.inputs.adminHours} min={0} max={120} value={adminHours} onChange={setAdminHours} />
          <RoiInput id="roi-admin-rate" label={content.inputs.adminRate} min={0} max={10000} value={adminRate} prefix={currency} onChange={setAdminRate} />
          <RoiInput id="roi-supervisor-hours" label={content.inputs.supervisorHours} min={0} max={80} value={supervisorHours} onChange={setSupervisorHours} />
          <RoiInput id="roi-approval-hours" label="Timesheet approval hours per week" min={0} max={1000} value={approvalHours} onChange={setApprovalHours} />
          <RoiInput id="roi-correction-frequency" label="Payroll corrections per week (optional)" min={0} max={1000} value={correctionFrequency} onChange={setCorrectionFrequency} />
          <RoiInput id="roi-correction-cost" label="Cost per payroll correction (optional)" min={0} max={10000} value={correctionCost} onChange={setCorrectionCost} />
          <RoiInput id="roi-reduction" label="Expected repetitive-work reduction assumption (%)" min={0} max={100} value={reductionPercent} onChange={setReductionPercent} />
        </div>

        <div className="landing-roi-calculator__results" aria-live="polite">
          <div className="landing-roi-result landing-roi-result--primary">
            <span>{content.results.weekly}</span>
            <strong>{formatCurrency(results.weekly, locale,currency)}</strong>
          </div>
          <div className="landing-roi-result">
            <span>{content.results.monthly}</span>
            <strong>{formatCurrency(results.monthly, locale,currency)}</strong>
          </div>
          <div className="landing-roi-result">
            <span>{content.results.annual}</span>
            <strong>{formatCurrency(results.annual, locale,currency)}</strong>
          </div>
          <div className="landing-roi-result landing-roi-result--estimate">
            <span>Potential annual savings at {reductionPercent}% reduction</span>
            <strong>{formatCurrency(results.reclaimed, locale,currency)}</strong>
          </div>
          <p className="landing-roi-formula">Weekly baseline = (admin + supervisor + approval hours) × admin hourly cost + corrections × correction cost. Assumptions: supervisor and approval hourly rates equal the entered admin rate; year = 52 weeks; month = 52/12 weeks. Potential savings = baseline × entered reduction percentage.</p>
          <p>User-supplied inputs and explicit reduction assumptions produce scenario estimates. No actual customer measurement or guaranteed saving is represented. Selected {period} potential savings: {formatCurrency(calculation.potentialSavings[period],locale,currency)}.</p>
          <p>Savings represent gross potential labor-cost opportunity before software, services or implementation costs; they are not measured financial returns.</p>
          <p className="landing-roi-note">{content.note}</p>
          <form className="landing-roi-report" onSubmit={submitReport}>
            <p>Calculations are anonymous. Submit only to request a report containing your current calculation inputs and estimates. This does not subscribe you to commercial messages.</p>
            <input className="input" placeholder="Company" value={reportForm.company} onChange={(event) => setReportForm({ ...reportForm, company: event.target.value })} required />
            <input className="input" placeholder="Contact" value={reportForm.contact} onChange={(event) => setReportForm({ ...reportForm, contact: event.target.value })} required />
            <input className="input" type="email" placeholder="Email" value={reportForm.email} onChange={(event) => setReportForm({ ...reportForm, email: event.target.value })} required />
            <input className="input" placeholder="Industry" value={reportForm.industry} onChange={(event) => setReportForm({ ...reportForm, industry: event.target.value })} />
            <label><input type="checkbox" checked={followUpConsent} onChange={event=>setFollowUpConsent(event.target.checked)}/>I would also like commercial follow-up.</label>
            <button className="btn btn-primary" type="submit" disabled={reportStatus === 'loading' || tools?.roi.enabled===false}>
              {reportStatus === 'loading' ? 'Sending...' : 'Request ROI report'}
            </button>
            {reportStatus === 'success' ? <p className="landing-contact__status landing-contact__status--success">ROI report request received.</p> : null}
            {reportStatus === 'error' ? <p className="landing-contact__status landing-contact__status--error">Unable to send the report request.</p> : null}
          </form>
          <div className="landing-roi-links" aria-label={content.linksTitle}>
            {content.links.map((link) => (
              <a key={link.href} href={link.href}>{link.label}</a>
            ))}
          </div>
        </div>
        {!tools&&!toolsError?<p role="status">Loading staffing tool availability…</p>:null}
        {toolsError?<p role="alert">Unable to load staffing tool availability. ROI arithmetic remains available; retry by reloading.</p>:null}
        {tools?.roi.enabled===false?<p>ROI report capture is disabled for this site. Existing anonymous arithmetic remains available.</p>:null}
        {tools?.staffing.enabled?<StaffingPlanningTools currency={currency} locale={locale} onScenarios={setOtherScenarios}/>:tools?<p>Staffing and planning tools are disabled for this site.</p>:null}
      </div>
    </section>
  )
}
